import React, { FC, useState, useEffect } from "react";
import { LibraryTechnology, TechnologyUnit } from "~/design-system/";
import {
  CreateLibraryTechnologyMutationVariables,
  CreateTechnologyUnitMutationVariables,
  UpdateLibraryTechnologiesMutationVariables,
  UpdateTechnologyUnitsMutationVariables,
  TechnologyChargeType,
  DeleteTechnologyUnitMutationVariables,
  UpdateManyTechnologyRatesMutationVariables,
  UpdateTechnologyRatesInput,
  CreateManyTechnologyRatesMutationVariables,
  CreateManyTechnologyRatesInput,
} from "~/gql/graphql";
import { Button, Tabs, Table, Form, Modal, Input, Select, Alert, Tooltip } from "antd";
import { PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { useForm } from "antd/lib/form/Form";
import { compareAsc } from "date-fns";
import produce from "immer";
import { PercentageInput, PriceInput } from "~/design-system/components/input-number";
import { Icons } from "@design-system";

export type TechnologyDataImportProps = {
  libraryTechnologies: LibraryTechnology[];
  technologyUnits: TechnologyUnit[];
  currencies: { id: number; code: string; symbol: string; baseCurrency: boolean | null }[];
  createLibraryTechnology: (data: {
    variables: CreateLibraryTechnologyMutationVariables;
    onCompleted: () => void;
  }) => void;
  createTechnologyUnit: (data: {
    variables: CreateTechnologyUnitMutationVariables;
    onCompleted: () => void;
  }) => void;
  updateLibraryTechnology: (data: {
    variables: UpdateLibraryTechnologiesMutationVariables;
    onCompleted: () => void;
  }) => void;
  updateTechnologyUnit: (data: { variables: UpdateTechnologyUnitsMutationVariables }) => void;
  deleteTechnologyUnit: (data: { variables: DeleteTechnologyUnitMutationVariables }) => void;
  updateManyTechnologyRates: (data: {
    variables: UpdateManyTechnologyRatesMutationVariables;
    onCompleted?: () => void;
  }) => void;
  createManyTechnologyRates: (data: {
    variables: CreateManyTechnologyRatesMutationVariables;
    onCompleted?: () => void;
  }) => void;
  updateLoading: boolean;
  queryLoading: boolean;
  currencySymbol: string;
  enableTechPricing: boolean;
};

type LibraryTechnologyUpdate = {
  id: number;
  description: string;
  type: TechnologyChargeType;
  technologyUnitId: number;
  changed: boolean;
  toDelete: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: { id: number; userName: string; displayName: string } | null;
  technologyRates: Array<TechnologyRateUpdate>;
};

type TechnologyRateUpdate = {
  id: number;
  currencyId: number;
  fixedAmount: number;
  baseAmount: number;
  percentage: number;
  unitCost: number;
  changed: boolean;
};

type TechnologyUnitUpdate = {
  id: number;
  name: string;
  changed: boolean;
  toDelete: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: { id: number; userName: string; displayName: string } | null;
};

const CHARGE_TYPE_DISPLAY: Record<TechnologyChargeType, string> = {
  [TechnologyChargeType.Fixed]: "Fixed",
  [TechnologyChargeType.Percentage]: "Percentage",
  [TechnologyChargeType.BasePlusUnits]: "Base Charge Plus Units",
};

export const TechnologyDataImport: FC<TechnologyDataImportProps> = ({
  libraryTechnologies,
  technologyUnits,
  currencies = [],
  createLibraryTechnology,
  createTechnologyUnit,
  updateLibraryTechnology,
  updateTechnologyUnit,
  deleteTechnologyUnit,
  updateManyTechnologyRates,
  createManyTechnologyRates,
  updateLoading,
  queryLoading,
  currencySymbol,
  enableTechPricing,
}) => {
  const [activeTab, setActiveTab] = useState("1");
  const [showAddNewModal, setShowAddNewModal] = useState(false);
  const [modalForm] = useForm();
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    const baseCurrency = currencies.find((c) => c.baseCurrency);
    return baseCurrency?.id || currencies[0]?.id;
  });

  useEffect(() => {
    setSelectedCurrency(currencies.find((c) => c.baseCurrency)?.id || currencies[0]?.id);
  }, [currencies]);

  const getInitialTechState = (technologies: LibraryTechnology[]) =>
    technologies.reduce((acc, curr) => {
      if (!curr) return acc;

      const techRates = (curr.technologyRates || []).map((rate) => ({
        id: rate.id,
        currencyId: rate.currencyId,
        fixedAmount: rate.fixedAmount,
        baseAmount: rate.baseAmount,
        percentage: rate.percentage,
        unitCost: rate.unitCost,
        changed: false,
      }));

      // Ensure all currencies have a rate
      currencies.forEach((currency) => {
        if (!techRates.some((rate) => rate.currencyId === currency.id)) {
          techRates.push({
            id: 0, // Temporary ID for new rates
            currencyId: currency.id,
            fixedAmount: 0,
            baseAmount: 0,
            percentage: 0,
            unitCost: 0,
            changed: false,
          });
        }
      });

      acc[curr.id] = {
        id: curr.id,
        description: curr.description,
        type: curr.type,
        technologyUnitId: curr.technologyUnit.id,
        changed: false,
        toDelete: false,
        createdAt: curr.createdAt,
        updatedAt: curr.updatedAt,
        createdBy: curr.createdBy,
        technologyRates: techRates,
      };
      return acc;
    }, {} as Record<number, LibraryTechnologyUpdate>);

  const getInitialUnitState = (units: TechnologyUnit[]) =>
    units.reduce((acc, curr) => {
      if (!curr) return acc;
      acc[curr.id] = {
        id: curr.id,
        name: curr.name,
        changed: false,
        toDelete: false,
        createdAt: curr.createdAt,
        updatedAt: curr.updatedAt,
        createdBy: curr.createdBy,
      };
      return acc;
    }, {} as Record<number, TechnologyUnitUpdate>);

  const [techFormState, setTechFormState] = useState<Record<number, LibraryTechnologyUpdate>>({});
  const [unitFormState, setUnitFormState] = useState<Record<number, TechnologyUnitUpdate>>({});

  useEffect(() => {
    if (libraryTechnologies?.length > 0 && currencies?.length > 0) {
      setTechFormState(getInitialTechState(libraryTechnologies));
    }
  }, [libraryTechnologies, currencies]);

  useEffect(() => {
    if (technologyUnits?.length > 0) {
      setUnitFormState(getInitialUnitState(technologyUnits));
    }
  }, [technologyUnits]);

  const loading = updateLoading || queryLoading;

  const handleModalOk = async () => {
    modalForm
      .validateFields()
      .then(async (values) => {
        if (activeTab === "1") {
          createLibraryTechnology({
            variables: {
              data: {
                description: values.description,
                type: values.type,
                technologyUnit: {
                  connect: { id: values.technologyUnitId },
                },
              },
            },
            onCompleted: () => setShowAddNewModal(false),
          });
        } else if (activeTab === "3") {
          createTechnologyUnit({
            variables: {
              data: {
                name: values.name,
              },
            },
            onCompleted: () => setShowAddNewModal(false),
          });
        }
      })
      .catch((err) => {
        console.log({ err });
      });
  };

  const onSubmit = async () => {
    if (activeTab === "1") {
      const changed = Object.values(techFormState).filter((x) => x.changed === true);
      if (changed.length === 0) return;

      updateLibraryTechnology({
        variables: {
          data: changed.map((x) => ({
            id: x.id,
            data: {
              description: x.description,
              type: x.type,
              technologyUnitId: x.technologyUnitId,
              deletedAt: x.toDelete ? new Date() : null,
            },
          })),
        },
        onCompleted: () => {
          setTechFormState(
            produce(techFormState, (draft) => {
              changed.forEach((item) => {
                draft[item.id].changed = false;
              });
            })
          );
        },
      });
    } else if (activeTab === "2") {
      const ratesToUpdate: UpdateTechnologyRatesInput[] = [];
      const ratesToCreate: CreateManyTechnologyRatesInput = { data: [] };
      const updatedTechIds = new Set<number>();

      Object.values(techFormState).forEach((tech) => {
        tech.technologyRates.forEach((rate) => {
          if (rate.changed) {
            updatedTechIds.add(tech.id);
            if (rate.id > 0) {
              ratesToUpdate.push({
                id: rate.id,
                data: {
                  fixedAmount: rate.fixedAmount,
                  baseAmount: rate.baseAmount,
                  percentage: rate.percentage,
                  unitCost: rate.unitCost,
                },
              });
            } else {
              ratesToCreate.data.push({
                libraryTechnologyId: tech.id,
                currencyId: rate.currencyId,
                fixedAmount: rate.fixedAmount,
                baseAmount: rate.baseAmount,
                percentage: rate.percentage,
                unitCost: rate.unitCost,
              });
            }
          }
        });
      });

      if (ratesToUpdate.length === 0 && ratesToCreate.data.length === 0) return;

      const promises = [];

      if (ratesToUpdate.length > 0) {
        promises.push(
          updateManyTechnologyRates({
            variables: {
              data: {
                items: ratesToUpdate,
              },
            },
          })
        );
      }

      if (ratesToCreate.data.length > 0) {
        promises.push(
          createManyTechnologyRates({
            variables: {
              data: ratesToCreate,
            },
          })
        );
      }

      try {
        await Promise.all(promises);
        setTechFormState(
          produce(techFormState, (draft) => {
            updatedTechIds.forEach((techId) => {
              draft[techId].technologyRates.forEach((rate) => {
                rate.changed = false;
              });
            });
          })
        );
      } catch (error) {
        console.error("Error saving technology rates:", error);
      }
    } else if (activeTab === "3") {
      const changedUnits = Object.values(unitFormState).filter((x) => x.changed === true);
      if (changedUnits.length === 0) return;

      const toDelete = changedUnits.filter((x) => x.toDelete);
      const toUpdate = changedUnits.filter((x) => !x.toDelete);

      const promises = [];

      if (toDelete.length > 0) {
        const deletePromises = toDelete.map((unit) =>
          deleteTechnologyUnit({
            variables: {
              id: unit.id,
            },
          })
        );
        promises.push(Promise.allSettled(deletePromises));
      }

      if (toUpdate.length > 0) {
        promises.push(
          updateTechnologyUnit({
            variables: {
              data: toUpdate.map((x) => ({
                id: x.id,
                data: {
                  name: x.name,
                },
              })),
            },
          })
        );
      }

      try {
        await Promise.all(promises);
        setUnitFormState(
          produce(unitFormState, (draft) => {
            changedUnits.forEach((item) => {
              if (draft[item.id] && !item.toDelete) {
                draft[item.id].changed = false;
              } else if (item.toDelete) {
                delete draft[item.id];
              }
            });
          })
        );
      } catch (error) {
        console.error("Error updating technology units:", error);
      }
    }
  };

  const getTechnologyColumns = () => [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30rem",
      render: (val: string, record: LibraryTechnologyUpdate) => (
        <Input
          value={val}
          onChange={(e) => {
            setTechFormState(
              produce(techFormState, (draft) => {
                draft[record.id].description = e.target.value;
                draft[record.id].changed = true;
              })
            );
          }}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "15rem",
      render: (val: TechnologyChargeType, record: LibraryTechnologyUpdate) => (
        <Select
          value={val}
          style={{ width: "200px" }}
          onChange={(value) => {
            setTechFormState(
              produce(techFormState, (draft) => {
                draft[record.id].type = value;
                draft[record.id].changed = true;
              })
            );
          }}
          options={Object.values(TechnologyChargeType).map((type) => ({
            value: type,
            label: CHARGE_TYPE_DISPLAY[type],
          }))}
        />
      ),
    },
    {
      title: "Unit",
      dataIndex: "technologyUnitId",
      key: "technologyUnitId",
      width: "15rem",
      render: (val: number, record: LibraryTechnologyUpdate) => (
        <Select
          value={val}
          onChange={(value) => {
            setTechFormState(
              produce(techFormState, (draft) => {
                draft[record.id].technologyUnitId = value;
                draft[record.id].changed = true;
              })
            );
          }}
          options={technologyUnits.map((unit) => ({
            value: unit.id,
            label: unit.name,
          }))}
        />
      ),
    },
    {
      title: "Last updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (val: string) => new Date(val).toLocaleString(),
      sorter: (a: any, b: any) => compareAsc(new Date(a.updatedAt), new Date(b.updatedAt)),
      width: "15rem",
    },
    {
      title: "Created by",
      dataIndex: ["createdBy", "displayName"],
      key: "createdBy",
      width: "15rem",
    },
    {
      title: Icons.Delete,
      dataIndex: "delete",
      key: "delete",
      width: "10rem",
      render: (_: any, record: LibraryTechnologyUpdate) => (
        <input
          type="checkbox"
          checked={techFormState[record.id]?.toDelete ?? false}
          onChange={(e) => {
            setTechFormState(
              produce(techFormState, (draft) => {
                draft[record.id].toDelete = e.target.checked;
                draft[record.id].changed = true;
              })
            );
          }}
        />
      ),
    },
  ];

  const getUnitColumns = () => [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30rem",
      render: (val: string, record: TechnologyUnitUpdate) => (
        <Input
          value={val}
          onChange={(e) => {
            setUnitFormState(
              produce(unitFormState, (draft) => {
                draft[record.id].name = e.target.value;
                draft[record.id].changed = true;
              })
            );
          }}
        />
      ),
    },
    {
      title: "Last updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (val: string) => new Date(val).toLocaleString(),
      sorter: (a: any, b: any) => compareAsc(new Date(a.updatedAt), new Date(b.updatedAt)),
      defaultSortOrder: "descend" as const,
      width: "15rem",
    },
    {
      title: "Created by",
      dataIndex: ["createdBy", "displayName"],
      key: "createdBy",
      width: "15rem",
    },
    {
      title: Icons.Delete,
      dataIndex: "delete",
      key: "delete",
      width: "10rem",
      render: (_: any, record: TechnologyUnitUpdate) => (
        <input
          type="checkbox"
          checked={unitFormState[record.id]?.toDelete ?? false}
          onChange={(e) => {
            setUnitFormState(
              produce(unitFormState, (draft) => {
                draft[record.id].toDelete = e.target.checked;
                draft[record.id].changed = true;
              })
            );
          }}
        />
      ),
    },
  ];

  const getTechnologyRateColumns = () => [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "25rem",
      render: (_: unknown, record: LibraryTechnologyUpdate) => record.description,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "15rem",
      render: (val: TechnologyChargeType) => (
        <Tooltip title="Navigate to the Technologies tab to change the Technology Pricing Type">
          {CHARGE_TYPE_DISPLAY[val]}
        </Tooltip>
      ),
    },
    {
      title: "Fixed Amount",
      dataIndex: "fixedAmount",
      key: "fixedAmount",
      width: "15rem",
      render: (_: unknown, record: LibraryTechnologyUpdate) => {
        const rate = record.technologyRates.find((r) => r.currencyId === selectedCurrency);
        if (!rate) return null;

        return record.type === TechnologyChargeType.Fixed ? (
          <PriceInput
            value={rate.fixedAmount}
            onChange={(value) => {
              setTechFormState(
                produce(techFormState, (draft) => {
                  const rateIndex = draft[record.id].technologyRates.findIndex(
                    (r) => r.currencyId === selectedCurrency
                  );
                  if (rateIndex >= 0) {
                    draft[record.id].technologyRates[rateIndex].fixedAmount = value || 0;
                    draft[record.id].technologyRates[rateIndex].changed = true;
                  }
                })
              );
            }}
            currencySymbol={
              currencies.find((c) => c.id === selectedCurrency)?.symbol || currencySymbol
            }
          />
        ) : null;
      },
    },
    {
      title: "Base Amount",
      dataIndex: "baseAmount",
      key: "baseAmount",
      width: "15rem",
      render: (_: unknown, record: LibraryTechnologyUpdate) => {
        const rate = record.technologyRates.find((r) => r.currencyId === selectedCurrency);
        if (!rate) return null;

        return record.type === TechnologyChargeType.BasePlusUnits ? (
          <PriceInput
            value={rate.baseAmount}
            onChange={(value) => {
              setTechFormState(
                produce(techFormState, (draft) => {
                  const rateIndex = draft[record.id].technologyRates.findIndex(
                    (r) => r.currencyId === selectedCurrency
                  );
                  if (rateIndex >= 0) {
                    draft[record.id].technologyRates[rateIndex].baseAmount = value || 0;
                    draft[record.id].technologyRates[rateIndex].changed = true;
                  }
                })
              );
            }}
            currencySymbol={
              currencies.find((c) => c.id === selectedCurrency)?.symbol || currencySymbol
            }
          />
        ) : null;
      },
    },
    {
      title: "Unit Cost",
      dataIndex: "unitCost",
      key: "unitCost",
      width: "15rem",
      render: (_: unknown, record: LibraryTechnologyUpdate) => {
        const rate = record.technologyRates.find((r) => r.currencyId === selectedCurrency);
        if (!rate) return null;

        return record.type === TechnologyChargeType.BasePlusUnits ? (
          <PriceInput
            value={rate.unitCost}
            onChange={(value) => {
              setTechFormState(
                produce(techFormState, (draft) => {
                  const rateIndex = draft[record.id].technologyRates.findIndex(
                    (r) => r.currencyId === selectedCurrency
                  );
                  if (rateIndex >= 0) {
                    draft[record.id].technologyRates[rateIndex].unitCost = value || 0;
                    draft[record.id].technologyRates[rateIndex].changed = true;
                  }
                })
              );
            }}
            currencySymbol={
              currencies.find((c) => c.id === selectedCurrency)?.symbol || currencySymbol
            }
          />
        ) : null;
      },
    },
    {
      title: "Percentage",
      dataIndex: "percentage",
      key: "percentage",
      width: "15rem",
      render: (_: unknown, record: LibraryTechnologyUpdate) => {
        const rate = record.technologyRates.find((r) => r.currencyId === selectedCurrency);
        if (!rate) return null;

        return record.type === TechnologyChargeType.Percentage ? (
          <PercentageInput
            value={rate.percentage}
            onChange={(value) => {
              setTechFormState(
                produce(techFormState, (draft) => {
                  const rateIndex = draft[record.id].technologyRates.findIndex(
                    (r) => r.currencyId === selectedCurrency
                  );
                  if (rateIndex >= 0) {
                    draft[record.id].technologyRates[rateIndex].percentage = value || 0;
                    draft[record.id].technologyRates[rateIndex].changed = true;
                  }
                })
              );
            }}
          />
        ) : null;
      },
    },
  ];

  const renderModalContent = () => {
    if (activeTab === "1") {
      return (
        <>
          <Form.Item rules={[{ required: true }]} name="description" label="Description">
            <Input />
          </Form.Item>
          <Form.Item rules={[{ required: true }]} name="type" label="Type">
            <Select
              style={{ width: "200px" }}
              options={Object.values(TechnologyChargeType).map((type) => ({
                value: type,
                label: CHARGE_TYPE_DISPLAY[type],
              }))}
            />
          </Form.Item>
          <Form.Item rules={[{ required: true }]} name="technologyUnitId" label="Unit">
            <Select
              options={technologyUnits.map((unit) => ({
                value: unit.id,
                label: unit.name,
              }))}
            />
          </Form.Item>
        </>
      );
    } else if (activeTab === "3") {
      return (
        <Form.Item rules={[{ required: true }]} name="name" label="Name">
          <Input />
        </Form.Item>
      );
    }

    return null;
  };

  const renderCurrencySelector = () => {
    return (
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
        <span style={{ marginRight: 8, alignSelf: "center" }}>Currency:</span>
        <Select
          value={selectedCurrency}
          onChange={setSelectedCurrency}
          style={{ width: 120 }}
          options={currencies.map((c) => ({
            value: c.id,
            label: `${c.code} (${c.symbol})`,
          }))}
        />
      </div>
    );
  };

  return (
    <>
      {!enableTechPricing && (
        <Alert
          message="Technology pricing is disabled. Enable it in App Settings to use technology pricing in a proposal."
          type="warning"
        />
      )}
      <div>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="Technologies" key="1">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 5,
                marginTop: 10,
              }}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={loading || showAddNewModal}
                onClick={() => setShowAddNewModal(true)}
              >
                Add New
              </Button>
              <Button
                size="large"
                disabled={Object.values(techFormState).every((x) => x.changed === false)}
                type="primary"
                icon={<SaveOutlined />}
                onClick={onSubmit}
              >
                Save
              </Button>
            </div>
            <Table
              columns={getTechnologyColumns()}
              dataSource={Object.values(techFormState)}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: [10],
                onChange() {
                  if (Object.values(techFormState).every((x) => x.changed === false)) return;
                  onSubmit();
                },
              }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Technology Rates" key="2">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 5,
                marginTop: 10,
              }}
            >
              {renderCurrencySelector()}
              <Button
                size="large"
                disabled={
                  !Object.values(techFormState).some((tech) =>
                    tech.technologyRates.some((rate) => rate.changed)
                  )
                }
                type="primary"
                icon={<SaveOutlined />}
                onClick={onSubmit}
              >
                Save
              </Button>
            </div>
            <Table
              columns={getTechnologyRateColumns()}
              dataSource={Object.values(techFormState)}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: [10],
                onChange() {
                  if (
                    !Object.values(techFormState).some((tech) =>
                      tech.technologyRates.some((rate) => rate.changed)
                    )
                  )
                    return;
                  onSubmit();
                },
              }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Technology Units" key="3">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 5,
                marginTop: 10,
              }}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={loading || showAddNewModal}
                onClick={() => setShowAddNewModal(true)}
              >
                Add New
              </Button>
              <Button
                size="large"
                disabled={Object.values(unitFormState).every((x) => x.changed === false)}
                type="primary"
                icon={<SaveOutlined />}
                onClick={onSubmit}
              >
                Save
              </Button>
            </div>
            <Table
              columns={getUnitColumns()}
              dataSource={Object.values(unitFormState)}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: [10],
                onChange() {
                  if (Object.values(unitFormState).every((x) => x.changed === false)) return;
                  onSubmit();
                },
              }}
            />
          </Tabs.TabPane>
        </Tabs>
      </div>

      <Modal
        title={`Create New ${
          activeTab === "1" ? "Technology" : activeTab === "3" ? "Technology Unit" : ""
        }`}
        centered
        width={700}
        open={showAddNewModal}
        onOk={handleModalOk}
        onCancel={() => setShowAddNewModal(false)}
        destroyOnClose
      >
        <Form preserve={false} form={modalForm}>
          {renderModalContent()}
        </Form>
      </Modal>
    </>
  );
};
